import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Textarea,
  VStack,
  Text,
  HStack,
  Flex,
  Input,
  useToast,
  Checkbox,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Divider,
} from '@chakra-ui/react';

interface WritingStyle {
  id: string;
  name: string;
  instruction: string;
  isDefault?: boolean;
}

interface WritingStyleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (style: WritingStyle) => void;
  editingStyle?: WritingStyle | null;
}

// デフォルトのライティングスタイル
const defaultStyles: WritingStyle[] = [
  {
    id: 'casual-ore',
    name: '一人称「俺」カジュアル',
    instruction: '一人称は「俺」を使用し、カジュアルで親しみやすい口調で書いてください。',
    isDefault: true,
  },
  {
    id: 'polite-desumasuchou',
    name: 'ですます調（丁寧）',
    instruction: 'ですます調を使用し、丁寧で読みやすい文章にしてください。',
    isDefault: true,
  },
  {
    id: 'catchy-phrases',
    name: 'キャッチーフレーズ散りばめ',
    instruction: 'キャッチーなフレーズや印象的な表現を適度に散りばめて、読者の興味を引く文章にしてください。',
    isDefault: true,
  },
  {
    id: 'technical-detailed',
    name: '技術的・詳細解説',
    instruction: '技術的な内容を詳しく解説し、初心者にも分かりやすく説明してください。具体例やコードも含めてください。',
    isDefault: true,
  },
  {
    id: 'emotional-empathy',
    name: '感情豊か・共感重視',
    instruction: '感情的な表現を使い、読者に共感してもらえるような温かみのある文章にしてください。',
    isDefault: true,
  },
  {
    id: 'business-professional',
    name: 'ビジネス・プロフェッショナル',
    instruction: 'ビジネスシーンに適した、プロフェッショナルで簡潔な表現を使用してください。',
    isDefault: true,
  },
];

const WritingStyleModal: React.FC<WritingStyleModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingStyle,
}) => {
  const [name, setName] = useState('');
  const [instruction, setInstruction] = useState('');
  const [selectedDefaults, setSelectedDefaults] = useState<string[]>([]);
  const toast = useToast();

  useEffect(() => {
    if (editingStyle) {
      setName(editingStyle.name);
      setInstruction(editingStyle.instruction);
      setSelectedDefaults([]);
    } else {
      setName('');
      setInstruction('');
      setSelectedDefaults([]);
    }
  }, [editingStyle, isOpen]);

  const handleDefaultToggle = (styleId: string) => {
    setSelectedDefaults(prev => 
      prev.includes(styleId) 
        ? prev.filter(id => id !== styleId)
        : [...prev, styleId]
    );
  };

  const handleRemoveDefault = (styleId: string) => {
    setSelectedDefaults(prev => prev.filter(id => id !== styleId));
  };

  const generateCombinedInstruction = () => {
    const selectedInstructions = defaultStyles
      .filter(style => selectedDefaults.includes(style.id))
      .map(style => style.instruction);
    
    const combined = [...selectedInstructions, instruction.trim()]
      .filter(Boolean)
      .join(' ');
    
    return combined;
  };

  const handleSave = () => {
    const combinedInstruction = generateCombinedInstruction();
    
    if (!name.trim()) {
      toast({
        title: 'エラー',
        description: 'スタイル名を入力してください',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!combinedInstruction) {
      toast({
        title: 'エラー',
        description: 'スタイル指示またはデフォルトスタイルを選択してください',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const writingStyle: WritingStyle = {
      id: editingStyle?.id || Date.now().toString(),
      name: name.trim(),
      instruction: combinedInstruction,
    };

    onSave(writingStyle);
    onClose();
    
    toast({
      title: editingStyle ? 'スタイルを更新しました' : 'スタイルを保存しました',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleClose = () => {
    setName('');
    setInstruction('');
    setSelectedDefaults([]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {editingStyle ? 'ライティングスタイルを編集' : 'ライティングスタイルを作成'}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <HStack>
              <Text fontWeight="bold" minW="100px">スタイル名:</Text>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="例: 関西弁カジュアル、技術記事用"
              />
            </HStack>

            <Divider />

            <VStack align="stretch" spacing={3}>
              <Text fontWeight="bold">デフォルトスタイル（組み合わせ可能）:</Text>
              
              <VStack align="stretch" spacing={2}>
                {defaultStyles.map(style => (
                  <Checkbox
                    key={style.id}
                    isChecked={selectedDefaults.includes(style.id)}
                    onChange={() => handleDefaultToggle(style.id)}
                  >
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="medium">{style.name}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {style.instruction}
                      </Text>
                    </VStack>
                  </Checkbox>
                ))}
              </VStack>

              {selectedDefaults.length > 0 && (
                <VStack align="stretch" spacing={2}>
                  <Text fontSize="sm" fontWeight="bold" color="blue.600">
                    選択中のスタイル:
                  </Text>
                  <Wrap>
                    {selectedDefaults.map(styleId => {
                      const style = defaultStyles.find(s => s.id === styleId);
                      return style ? (
                        <WrapItem key={styleId}>
                          <Tag size="md" variant="solid" colorScheme="blue">
                            <TagLabel>{style.name}</TagLabel>
                            <TagCloseButton onClick={() => handleRemoveDefault(styleId)} />
                          </Tag>
                        </WrapItem>
                      ) : null;
                    })}
                  </Wrap>
                </VStack>
              )}
            </VStack>

            <Divider />

            <VStack align="stretch" spacing={2}>
              <Flex justify="space-between" align="center">
                <Text fontWeight="bold">追加のカスタム指示:</Text>
                <Text fontSize="xs" color={instruction.length > 800 ? "red.500" : "gray.500"}>
                  {instruction.length.toLocaleString()} / 1,000
                </Text>
              </Flex>
              <Textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                maxLength={1000}
                placeholder={`例:
- 関西弁を使って親しみやすく
- 絵文字を適度に使用
- 具体的な数値データを重視
- 読者への問いかけを含める`}
                rows={6}
                resize="vertical"
              />
              <Text fontSize="sm" color="gray.500">
                デフォルトスタイルに加えて、独自の指示を追加できます。
              </Text>
            </VStack>

            {(selectedDefaults.length > 0 || instruction.trim()) && (
              <VStack align="stretch" spacing={2}>
                <Text fontSize="sm" fontWeight="bold" color="green.600">
                  最終的な指示（プレビュー）:
                </Text>
                <Text
                  fontSize="sm"
                  p={3}
                  bg="green.50"
                  borderRadius="md"
                  border="1px"
                  borderColor="green.200"
                  maxH="100px"
                  overflowY="auto"
                >
                  {generateCombinedInstruction()}
                </Text>
              </VStack>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            キャンセル
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            {editingStyle ? '更新' : '保存'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WritingStyleModal;